import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Label,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { HeroiconsOutlineCalendar } from "../../assets/icons/LayoutIcon";
import Loading from "../Layout/Loading";
import { getDashboard } from "../../services/dashboardService";
import dayjs from "dayjs"; // Make sure to install this: npm install dayjs

const SalesBarChart = ({ setSalesData }) => {
  const [activeTab, setActiveTab] = useState("Week");
  const [filteredData, setFilteredData] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });
  const [displayDateRange, setDisplayDateRange] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Set on first load
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const productCount = filteredData.length;
  const limit = isMobile ? 1 : 1;
  const shouldScroll = productCount > limit;
  const chartWidth = shouldScroll ? (productCount - limit) * 90 : "100%";

  // Function to calculate Y-axis width based on the maximum value
  const calculateYAxisWidth = (data) => {
    if (!data || data.length === 0) return 80; // Default width

    const maxValue = Math.max(...data.map((item) => item.sales || 0));

    // Calculate the number of digits in the maximum value
    const maxValueString = maxValue.toString();
    const digitCount = maxValueString.length;

    // Calculate approximate width based on digit count
    // Each digit is approximately 8-10 pixels wide, plus currency symbol
    const charWidth = 10;
    const currencySymbolWidth = 15;
    const padding = 25;
    const calculatedWidth =
      digitCount * charWidth + currencySymbolWidth + padding;

    // Set minimum and maximum bounds
    const minWidth = 80;
    const maxWidth = 200;

    return Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
  };

  // Get dynamic Y-axis width
  const yAxisWidth = calculateYAxisWidth(filteredData);

  // Truncate X-axis labels to a specific length
  const truncateLabel = (label, maxLength = 10) => {
    if (label.length <= maxLength) return label;
    return `${label.substring(0, maxLength)}...`;
  };

  // Format date to readable string (e.g., 5 Sep 2024)
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Handle date input changes

  const getDefaultDateRange = (tab) => {
    const today = dayjs();
    let fromDate;
    if (tab === "Week") fromDate = today.subtract(7, "day");
    else if (tab === "Month") fromDate = today.subtract(1, "month");
    else fromDate = today.subtract(1, "year");

    return {
      fromDate: fromDate.format("YYYY-MM-DD"),
      toDate: today.format("YYYY-MM-DD"),
    };
  };

  const fetchSalesData = async (tab, fromDate, toDate) => {
    setLoading(true);
    try {
      const response = await getDashboard({
        category: "sales tracking",
        start_date: fromDate,
        end_date: toDate,
      });

      if (response?.data?.response_data) {
        const mapped = response.data.response_data.map((item) => ({
          name: item.product_group,
          sales: item.Total_revenue,
        }));
        setFilteredData(mapped);
        setSalesData(mapped);
      }
    } catch (err) {
      console.error("Failed to fetch data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let fromDate = dateRange.fromDate;
    let toDate = dateRange.toDate;

    if (!fromDate || !toDate) {
      const defaultRange = getDefaultDateRange(activeTab);
      fromDate = defaultRange.fromDate;
      toDate = defaultRange.toDate;
      setDateRange(defaultRange);
      setDisplayDateRange(`${formatDate(fromDate)} - ${formatDate(toDate)}`);
    }

    fetchSalesData(activeTab, fromDate, toDate);
  }, [activeTab, dateRange]);

  const handleDateChange = (event) => {
    const { name, value } = event.target;

    setError("");

    // If fromDate is changed, reset toDate validation
    if (name === "fromDate") {
      // Set toDate to today's date if not selected
      const today = new Date().toISOString().split("T")[0];
      setDateRange({
        fromDate: value,
        toDate: value > today ? "" : today,
      });
    } else {
      setDateRange((prev) => ({ ...prev, toDate: value }));
    }
  };

  // Validate and finalize date range
  const handleApplyDateRange = () => {
    if (!dateRange.fromDate) {
      setError("Please select a start date");
      return;
    }

    const fromDate = new Date(dateRange.fromDate);
    const toDate = dateRange.toDate ? new Date(dateRange.toDate) : new Date();

    // Ensure toDate is not before fromDate
    if (toDate < fromDate) {
      setError("End date must be after start date");
      return;
    }

    // Format display string
    const formattedFromDate = formatDate(dateRange.fromDate);
    const formattedToDate = formatDate(dateRange.toDate);
    setDisplayDateRange(`${formattedFromDate} - ${formattedToDate}`);
    setIsDatePickerOpen(false);
    setError("");
  };

  if (loading) return <Loading />;
  const chartMargins = { top: 20, right: 30, left: 0, bottom: 50 };
  return (
    <div className="border border-gray-200 my-10 rounded-md p-5">
      <div className="justify-between lg:flex sm:mb-4 mb-0 items-baseline">
        <h1 className="text-text1 text-lg font-semibold">
          Sales vs Product Groups
        </h1>
        <div className="flex justify-between gap-2.5 items-center md:mb-0 mb-4">
          <div className="flex gap-2.5 h-12 items-center">
            {["Week", "Month", "Year"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setDateRange({
                    fromDate: "",
                    toDate: "",
                  });
                  setActiveTab(tab);
                }}
                className={`button ${
                  activeTab === tab ? "active" : "inactive"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="relative sm:block hidden">
            <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
              <HeroiconsOutlineCalendar className="text-md text-gray-500" />
            </div>
            <div className="pl-10">
              <button
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="flex items-center border rounded-r-md px-3 py-2 border-l-0 border-gray-300"
              >
                <span className="pl-2 text-xs">
                  {displayDateRange || "Select Date Range"}
                </span>
                {!displayDateRange &&
                  (isDatePickerOpen ? (
                    <ChevronUp className="ml-2 text-gray-500" size={16} />
                  ) : (
                    <ChevronDown className="ml-2 text-gray-500" size={16} />
                  ))}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isDatePickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="mt-2 w-[90%] sm:w-[30%] bg-white border rounded-md shadow-lg p-4 z-10">
            <div className="space-y-4 mb-4">
              <div className="flex-1">
                <label htmlFor="fromDate" className="primary_label">
                  From Date
                </label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                    <HeroiconsOutlineCalendar className="text-md text-gray-500" />
                  </div>
                  <div className="pl-10">
                    <input
                      type="date"
                      id="fromDate"
                      name="fromDate"
                      className="w-full custom-dateinput border rounded-md border-gray-300"
                      value={dateRange.fromDate}
                      onChange={handleDateChange}
                      max={dateRange.toDate}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <label htmlFor="toDate" className="primary_label">
                  To Date
                </label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                    <HeroiconsOutlineCalendar className="text-md text-gray-500" />
                  </div>
                  <div className="pl-10">
                    <input
                      type="date"
                      id="toDate"
                      name="toDate"
                      className="w-full custom-dateinput border rounded-md border-gray-300"
                      value={dateRange.toDate}
                      onChange={handleDateChange}
                      min={dateRange.fromDate}
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsDatePickerOpen(false)}
                className="border rounded-md px-3 py-2 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyDateRange}
                className="bg-primary text-white rounded-md px-3 py-2 hover:bg-primary"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex w-full overflow-x-auto ">
        <div
          className="sticky left-0 z-10 bg-white"
          style={{ width: `${yAxisWidth}px`, flexShrink: 0 }}
        >
          <ResponsiveContainer width="100%" height={370}>
            <BarChart data={filteredData} margin={chartMargins}>
              <YAxis
                tickCount={10}
                interval={0}
                axisLine={{ stroke: "#E2E8F0" }}
                tick={{ fill: "#656565", fontSize: 12 }}
                tickLine={{ stroke: "#E2E8F0" }}
                width={yAxisWidth}
                label={{
                  value: "Sales (₹)",
                  angle: -90,
                  position: "insideLeft",
                  offset: 5,
                  style: {
                    textAnchor: "middle",
                    fontSize: "12px",
                    fill: "#656565",
                  },
                }}
              />
              <Bar dataKey="sales" fill="transparent" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ minWidth: chartWidth, width: "100%" }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={filteredData} margin={chartMargins} barSize={40}>
              <CartesianGrid
                vertical={false}
                stroke="#F5F6F8"
                strokeDasharray="4 none"
              />
              <XAxis
                dataKey="name"
                stroke="#656565"
                interval={0}
                minTickGap={10}
                axisLine={{ stroke: "#E2E8F0" }}
                tick={{
                  fontSize: 11.5,
                  angle: -45,
                  textAnchor: "end",
                  dy: 10,
                  dx: -5,
                }}
                tickFormatter={(value) => truncateLabel(value, 6.5)}
              >
                <Label
                  // value="Product Groups"
                  position="insideBottom"
                  offset={-40}
                  style={{
                    fontSize: "12px",
                    fill: "#656565",
                    fontWeight: 600,
                  }}
                />
              </XAxis>
              <YAxis hide={true} />
              <Tooltip
                cursor={{ fill: "#FEE2E2", opacity: "0.7" }}
                content={({ active, payload }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="bg-white border p-2 rounded shadow">
                        <p className="font-semibold text-gray-900">
                          {payload[0].payload.name}
                        </p>
                        <p className="text-gray-900">
                          Sales: ₹
                          <span className="font-semibold">
                            {payload[0].value}
                          </span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="sales" fill="#BC251B" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <span className="text-sm">Product Groups</span>
      </div>
    </div>
  );
};

export default SalesBarChart;
